import { useEffect, useCallback, useRef } from "react";
import { ProfileFormData } from "../types/form";
import { saveProfileDraft } from "../utils/api";

const AUTO_SAVE_INTERVAL = 2000; // 2 seconds - more responsive
const STORAGE_KEY = "profileFormData";

export const useAutoSave = (
  formData: ProfileFormData,
  onEmailExists?: (email: string) => void,
  onOrganizationExists?: (email: string) => void,
  preventServerSave?: boolean,
  onSaveStatusChange?: (status: "idle" | "saving" | "saved") => void,
  isEmailBeingChanged?: boolean,
) => {
  const lastSavedData = useRef<string>("");
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastServerSaveRef = useRef<string>("");
  const isFirstLoad = useRef<boolean>(true);

  const saveToLocalStorage = useCallback(
    (data: ProfileFormData) => {
      try {
        // Convert File objects to base64 strings for storage
        const dataForStorage = { ...data };

        // Convert files to base64 if they exist
        if (data.headshot) {
          const reader = new FileReader();
          reader.onload = () => {
            // Create a new object with the converted headshot
            const dataWithConvertedHeadshot = {
              ...dataForStorage,
              headshot: reader.result as string,
            };
            localStorage.setItem(
              STORAGE_KEY,
              JSON.stringify(dataWithConvertedHeadshot),
            );
          };
          if (data.headshot instanceof File) {
            reader.readAsDataURL(data.headshot);
          }
        } else {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(dataForStorage));
        }

        lastSavedData.current = JSON.stringify(dataForStorage);
        // Silent save - no console logging for better UX

        // Notify status change
        if (onSaveStatusChange) {
          onSaveStatusChange("saved");
        }
      } catch (error) {
        console.error("Error saving form data to localStorage:", error);
      }
    },
    [onSaveStatusChange],
  );

  const saveToServer = useCallback(
    async (data: ProfileFormData, conflict: boolean = false) => {
      // Only save to server if email is provided
      if (!data.email || data.email.trim() === "") {
        return;
      }

      try {
        if (!conflict) {
          // Fire and forget - don't await the result
          saveProfileDraft(data)
            .then((result) => {
              if (result.success) {
                lastServerSaveRef.current = JSON.stringify(data);
              } else {
                // Silent error handling - only log in development
                if (process.env.NODE_ENV === "development") {
                  console.warn(
                    "Failed to save profile draft to server:",
                    result.error,
                  );
                }

                // If this is an email exists error, we need to notify the parent component
                if (result.error === "EMAIL_EXISTS") {
                  // Trigger callback to show email exists modal
                  if (onEmailExists) {
                    onEmailExists(data.email);
                  }
                }

                // If this is an organization exists error, we need to notify the parent component
                if (result.error === "ORGANIZATION_EXISTS") {
                  // Trigger callback to show organization exists modal
                  if (onOrganizationExists) {
                    onOrganizationExists(data.email);
                  }
                }
              }
            })
            .catch((error) => {
              // Silent error handling - only log in development
              if (process.env.NODE_ENV === "development") {
                console.error("Error saving profile draft to server:", error);
              }
            });
        } else {
          // Silent conflict handling - only log in development
          if (process.env.NODE_ENV === "development") {
            console.log(
              "There is a conflict, so not saving to server",
              conflict,
            );
          }
        }
      } catch (error) {
        // Silent error handling - only log in development
        if (process.env.NODE_ENV === "development") {
          console.error("Error initiating server save:", error);
        }
      }
    },
    [onEmailExists, onOrganizationExists],
  );

  const loadFromLocalStorage =
    useCallback((): Partial<ProfileFormData> | null => {
      try {
        const savedData = localStorage.getItem(STORAGE_KEY);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          return parsedData;
        }
      } catch (error) {
        console.error("Error loading form data from localStorage:", error);
      }
      return null;
    }, []);

  const clearSavedData = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      // Silent clear - only log in development
      if (process.env.NODE_ENV === "development") {
        console.log("Saved form data cleared");
      }
    } catch (error) {
      console.error("Error clearing saved data:", error);
    }
  }, []);

  // Auto-save effect
  useEffect(() => {
    const currentDataString = JSON.stringify(formData);

    // Only save if data has changed
    if (currentDataString !== lastSavedData.current) {
      
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Set new timeout
      autoSaveTimeoutRef.current = setTimeout(() => {
        // Notify that saving is starting
        if (onSaveStatusChange) {
          onSaveStatusChange("saving");
        }

        // Save to localStorage for immediate backup
        saveToLocalStorage(formData);

        // Mark first load as complete
        if (isFirstLoad.current) {
          isFirstLoad.current = false;
        }

        // Save to server if email is provided and not prevented
        if (
          formData.email &&
          formData.email.trim() !== "" &&
          !preventServerSave &&
          !isEmailBeingChanged
        ) {
          
          saveToServer(formData, preventServerSave);
        }
      }, AUTO_SAVE_INTERVAL);
    }

    // Cleanup timeout on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, preventServerSave, isEmailBeingChanged]);

  // Force save immediately
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const forceSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Save to localStorage immediately
    saveToLocalStorage(formData);

    // Save to server if email is provided and not prevented
    if (
      formData.email &&
      formData.email.trim() !== "" &&
      !preventServerSave &&
      !isEmailBeingChanged
    ) {
      saveToServer(formData, preventServerSave);
    } 
  }, [formData, preventServerSave, isEmailBeingChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    saveToLocalStorage,
    saveToServer,
    loadFromLocalStorage,
    clearSavedData,
    forceSave,
  };
};
